import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Field, Stack, Button, tokens, OptionCard } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { Form, Title } from '@hotjar/webapp/features/Registration/common/styles';
import { SubTitle } from '@hotjar/webapp/features/Login/styles';
import { USER_GOALS } from '@hotjar/webapp/domains/activation/user-personalization-info';
import { trackUsecaseSubmitted } from '@hotjar/webapp/domains/activation/user-personalization-info/tracking';
import { useCurrentUser } from '@hotjar/user';
import { useUserExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { PersonalisedTestimonialsLayout as Layout } from './PersonalisedTestimonials/PersonalisedTestimonialsLayout';
import { isInLocale } from '../utils/locales';
import { PersonalisedTestimonials } from './PersonalisedTestimonials/PersonalisedTestimonials';
import { getTestimonial } from './PersonalisedTestimonials/utils';
import { useOnboardingNavigation } from '../hooks/useOnboardingNavigation';

type UserId = number;

export interface OrganizationGoalProps {
  onSubmit: (goal: USER_GOALS) => void;
  // FIXME: Use this prop or remove it if not needed.
  // eslint-disable-next-line react/no-unused-prop-types
  userId: UserId;
  isSubmitInProgress?: boolean;
}

const FormWrapper = styled(Form)`
  max-width: 480px;
  width: 100%;
  margin: 0 auto;
`;

const StyledTitle = styled(Title)`
  margin-bottom: ${tokens.spacer.x1};
`;

const StyledSubTitle = styled(SubTitle)`
  margin-bottom: ${tokens.spacer.x2pt5};
`;

const StyledStack = styled(Stack)`
  margin-bottom: ${tokens.spacer.x3pt5};
`;

const StyledOptionCard = styled(OptionCard)`
  max-width: 223px;
  height: 144px;
  margin-bottom: 0;
  background-color: ${tokens.color.white};
  cursor: pointer;

  & > span:nth-of-type(1) {
    color: ${tokens.color.text_light};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin-top: 4px;
  }

  & > span:nth-of-type(2) {
    color: ${tokens.color.text_default};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  & svg {
    max-width: 22px;
    max-height: 22px;
    min-width: initial;
    min-height: initial;
  }
`;

export const StyledField = styled(Field)`
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: inline-flex;
`;

const NextButton = styled(Button)`
  width: fit-content;
  margin-right: ${tokens.spacer.x2};
}`;

const SkipButton = styled(Button)``;

const ONBOARDING_GOAL_STEP_UI_POSITION = 'onboarding-goal-step';

export const OrganizationGoal = ({
  onSubmit,
  isSubmitInProgress,
  userId,
}: OrganizationGoalProps): JSX.Element => {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const { getOrgDetails } = useOnboardingNavigation();
  const [goal, setGoal] = useState<USER_GOALS>(USER_GOALS.SKIPPED);
  const [isSkipInProgress, setIsSkipInProgress] = useState(false);
  const { data: user } = useCurrentUser();
  const { inVariant1: inPersonalisedTestimonialsExperiment } = useUserExperiment(
    'av_personalised_testimonials',
    user,
    isInLocale('en'),
  );

  const testimonial = getTestimonial({
    role: getOrgDetails(userId)?.role,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackUsecaseSubmitted({ usecase: goal }); // we keep the other tracking methods for now
    trackClick(goal, ONBOARDING_GOAL_STEP_UI_POSITION, {});
    trackClick('Use-case submitted', 'Next - Use-case Submitted', {});
    onSubmit(goal);
  };

  const handleSkip = () => {
    trackClick(USER_GOALS.SKIPPED, ONBOARDING_GOAL_STEP_UI_POSITION, {});
    setIsSkipInProgress(true);
    onSubmit(USER_GOALS.SKIPPED);
  };

  return (
    <Layout twoColumn={inPersonalisedTestimonialsExperiment} dataTestid="organization-steps-role">
      <FormWrapper onSubmit={handleSubmit} noValidate data-testid="organization-steps-goal">
        <StyledTitle aria-live="assertive">
          {t('activation:onboarding.organization-steps.goal.title')}
        </StyledTitle>
        <StyledSubTitle>
          {t('activation:onboarding.organization-steps.goal.subtitle')}
        </StyledSubTitle>
        <StyledStack preset="default" shouldWrap spacing="x2pt5">
          <StyledOptionCard
            name="goal-option-card"
            id={`option-card-${USER_GOALS.HEATMAPS}`}
            kind="single-select"
            title={t('activation:onboarding.organization-steps.goal.heatmaps.title')}
            description={t('activation:onboarding.organization-steps.goal.heatmaps.subtitle')}
            icon={USER_GOALS.HEATMAPS}
            value={USER_GOALS.HEATMAPS}
            onChange={() => setGoal(USER_GOALS.HEATMAPS)}
            isChecked={goal === USER_GOALS.HEATMAPS}
          />
          <StyledOptionCard
            name="goal-option-card"
            id={`option-card-${USER_GOALS.FEEDBACK}`}
            kind="single-select"
            title={t('activation:onboarding.organization-steps.goal.feedback.title')}
            description={t('activation:onboarding.organization-steps.goal.feedback.subtitle')}
            icon={USER_GOALS.FEEDBACK}
            value={USER_GOALS.FEEDBACK}
            onChange={() => setGoal(USER_GOALS.FEEDBACK)}
            isChecked={goal === USER_GOALS.FEEDBACK}
          />
          <StyledOptionCard
            name="goal-option-card"
            id={`option-card-${USER_GOALS.RECORDINGS}`}
            kind="single-select"
            title={t('activation:onboarding.organization-steps.goal.recordings.title')}
            description={t('activation:onboarding.organization-steps.goal.recordings.subtitle')}
            icon={USER_GOALS.RECORDINGS}
            value={USER_GOALS.RECORDINGS}
            onChange={() => setGoal(USER_GOALS.RECORDINGS)}
            isChecked={goal === USER_GOALS.RECORDINGS}
          />
          <StyledOptionCard
            name="goal-option-card"
            id={`option-card-${USER_GOALS.SURVEYS}`}
            kind="single-select"
            title={t('activation:onboarding.organization-steps.goal.surveys.title')}
            description={t('activation:onboarding.organization-steps.goal.surveys.subtitle')}
            icon={USER_GOALS.SURVEYS}
            value={USER_GOALS.SURVEYS}
            onChange={() => setGoal(USER_GOALS.SURVEYS)}
            isChecked={goal === USER_GOALS.SURVEYS}
          />
        </StyledStack>
        <ButtonWrapper>
          <NextButton
            size="l"
            type="submit"
            id="submit-step-goal"
            data-testid="submit-step-goal"
            isLoading={isSubmitInProgress && !isSkipInProgress}
            onClick={handleSubmit}
            isDisabled={!goal || isSkipInProgress}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
          >
            {t('activation:onboarding.organization-steps.submit-cta')}
          </NextButton>
          <SkipButton
            size="l"
            kind="secondary"
            id="skip-step-goal"
            data-testid="skip-step-goal"
            isLoading={isSkipInProgress}
            onClick={handleSkip}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
          >
            {t('activation:onboarding.organization-steps.skip-cta')}
          </SkipButton>
        </ButtonWrapper>
      </FormWrapper>
      {inPersonalisedTestimonialsExperiment ? (
        <PersonalisedTestimonials background="pointy" testimonial={testimonial} />
      ) : null}
    </Layout>
  );
};
